<template>
  <section class="admin-users-content">
    <GridBlockBase id="sessions-listing" :title="t('ui.layout.nav.users')">
      <template #icon>
        <UserIcon color="black" filled />
      </template>
      <template #body>
        <UsersTable :users="usersRef" :is-pending="isPendingRef" />
      </template>
      <template #headerExtra>
        <div class="filters-container">
          <SearchBar
            :placeholder="t('admin.users.listing.searchPlaceholder')"
            :value="searchRef"
            @emit:change="onSearchChange"
          />
          <CustomSelect
            id="user-role"
            :value="selectedRoleRef"
            :options="filerRoleOptionsRef"
            :label="t('ui.input.role.label')"
            hide-label
            @emit:change="onFilterRole"
            data-test-id="user-list-role-select"
          />
          <CustomSelect
            id="user-has-group"
            :value="selectedHasGroupRef"
            :options="filterHasGroupOptionsRef"
            :label="t('ui.filter.hasGroup')"
            hide-label
            @emit:change="onFilterHasGroup"
          />
        </div>

        <div
          class="btn-filters"
          role="button"
          tabindex="0"
          :aria-expanded="popoverIsOpenRef"
          @click.stop="openPopoverOnClick"
          @keyup.enter.prevent="openPopoverOnKey"
        >
          <FilterIcon color="black" filled />
          {{ t('ui.button.filters') }}
        </div>

        <Popover :popover="{ name: 'usersFilters', isOpen: popoverIsOpenRef, event: popoverEventRef || undefined }">
          <template #content>
            <SearchBar
              class="popover-search-bar"
              :placeholder="t('admin.users.listing.searchPlaceholder')"
              :value="searchRef"
              results-position="top"
              @emit:change="onSearchChange"
            />
            <CustomSelect
              id="user-role-mobile"
              :value="selectedRoleRef"
              :options="filerRoleOptionsRef"
              :label="t('ui.input.role.label')"
              hide-label
              placement="top"
              @emit:change="onFilterRole"
            />
            <CustomSelect
              id="user-has-group-mobile"
              :value="selectedHasGroupRef"
              :options="filterHasGroupOptionsRef"
              :label="t('ui.filter.hasGroup')"
              hide-label
              placement="top"
              @emit:change="onFilterHasGroup"
            />
          </template>
        </Popover>
        <CustomButton
          class="add-user-button"
          icon-position="right"
          color="primary"
          :text="t('admin.users.excelConfirmationModal.addUser')"
          usage="button"
          data-action="admin-users-import"
          data-test-id="admin-users-import"
        >
          <template #icon>
            <DropdownIcon color="black" />
          </template>
        </CustomButton>
      </template>
      <template #footer>
        <div>
          <span class="table-sub-info" data-test-id="admin-user-amount">
            {{
              t('ui.pagination.resultsOfOn', {
                start: Math.min(Math.max(1, perPageRef * (pageRef - 1) + 1), totalUsersRef ?? 1),
                end: Math.min(perPageRef * pageRef, totalUsersRef ?? 1),
                total: totalUsersRef,
              })
            }}
          </span>
        </div>
        <div class="page-select-wrapper">
          <CustomSelect
            placement="top"
            id="page-select"
            data-test-id="user-select-page"
            :value="`${pageRef}`"
            :options="pagesOptionsRef"
            :label="t('ui.pagination.page')"
            hide-label
            @emit:change="onPageChange"
          />
          <span class="table-sub-info table-sub-info__total-pages">{{
            t('ui.pagination.totalPages', { total: totalPagesRef })
          }}</span>
        </div>
        <MobilePagination :page="pageRef" :total="totalPagesRef ?? 1" @change="onPageChange" />
      </template>
    </GridBlockBase>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import useUsers from '@/features/admin/users/hooks/use-users.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import GridBlockBase from '@/features/ui/components/grid-block-base/grid-block-base.vue'
import DropdownIcon from '@/features/ui/components/icons/dropdown-icon.vue'
import FilterIcon from '@/features/ui/components/icons/filter-icon.vue'
import UserIcon from '@/features/ui/components/icons/user-icon.vue'
import MobilePagination from '@/features/ui/components/pagination/mobile-pagination.vue'
import Popover from '@/features/ui/components/popover/popover.vue'
import SearchBar from '@/features/ui/components/search-bar/search-bar.vue'
import CustomSelect from '@/features/ui/components/select/custom-select.vue'
import UsersTable from '@/features/ui/pages/admin/users/users-content/users-table/users-table.vue'
import usePopoverTrigger from '@/hooks/use-popover-trigger.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const searchRef = ref<string | null>(null)
const pageRef = ref<number>(1)
const perPageRef = ref<number>(20)

const {
  isOpenRef: popoverIsOpenRef,
  eventRef: popoverEventRef,
  openOnClick: openPopoverOnClick,
  openOnKey: openPopoverOnKey,
} = usePopoverTrigger()

// current selected role to filter
const selectedRoleRef = ref<RoleEnumTypeV2 | 'ALL'>('ALL')

// on select change, fitler the users
function onFilterRole(value: RoleEnumTypeV2 | 'ALL') {
  selectedRoleRef.value = value
}

const filerRoleOptionsRef = computed(
  () =>
    [
      { value: 'ALL', label: t('admin.users.listing.all') },
      { value: RoleEnumTypeV2.ADMIN, label: t('common.admin.capitalize', 2) },
      { value: RoleEnumTypeV2.MANAGER, label: t('common.manager.capitalize', 2) },
      { value: RoleEnumTypeV2.MENTOR, label: t('common.mentor.capitalize', 2) },
      { value: RoleEnumTypeV2.MENTEE, label: t('common.mentee.capitalize', 2) },
    ] satisfies { value: string; label: string }[],
)

// current selected has group to filter
const selectedHasGroupRef = ref<'ALL' | 'YES' | 'NO'>('ALL')

// on select change, fitler the users
function onFilterHasGroup(value: 'ALL' | 'YES' | 'NO') {
  selectedHasGroupRef.value = value
}

const filterHasGroupOptionsRef = computed(
  () =>
    [
      { value: 'ALL', label: t('admin.users.listing.withOrWithoutGroup') },
      { value: 'YES', label: t('admin.users.listing.withGroup') },
      { value: 'NO', label: t('admin.users.listing.withoutGroup') },
    ] satisfies { value: string; label: string }[],
)

const { usersRef, totalPagesRef, totalUsersRef, isPendingRef } = useUsers({
  pageRef,
  perPageRef,
  searchRef,
  selectedRoleRef,
  selectedHasGroupRef,
})

const pagesOptionsRef = computed(() => {
  return Array.from({ length: totalPagesRef.value ?? 0 }, (_, i) => ({
    label: `${t('ui.pagination.page')} ${i + 1}`,
    value: `${i + 1}`,
  })) satisfies { label: string; value: string }[]
})

function onSearchChange(value: string) {
  pageRef.value = 1
  searchRef.value = value
}

function onPageChange(value: number) {
  pageRef.value = value
}
</script>

<style lang="scss">
@import './users-content.scss';
@include mq('<desktop') {
  .table-sub-info__total-pages {
    display: none;
  }
}
</style>
